import { addDays, compareDates, type LocalDate } from '../dates';
import { dayDiff } from './dayMath';
import type { TaperSegment } from './types';

export interface TaperDraft {
  startLimit: number;
  step: number;
  everyDays: number;
  floor: number;
}

export type TaperError = 'bad_limit' | 'bad_step' | 'bad_every_days' | 'bad_floor';
export type QuitPhase = 'taper' | 'smoke_free';

export const MAX_PER_DAY = 100;

const isInt = (n: number, min: number, max: number) => Number.isInteger(n) && n >= min && n <= max;

export function validateTaper(d: TaperDraft): TaperError | null {
  if (!isInt(d.startLimit, 0, MAX_PER_DAY)) return 'bad_limit';
  if (!isInt(d.step, 0, 20)) return 'bad_step';
  if (!isInt(d.everyDays, 1, 60)) return 'bad_every_days';
  if (!isInt(d.floor, 0, d.startLimit)) return 'bad_floor';
  return null;
}

/** Start at the baseline and reach zero in about ten weeks: step = baseline / 10, every 7 days. */
export function proposeTaper(baselinePerDay: number): TaperDraft {
  const b = Math.max(1, Math.min(MAX_PER_DAY, Math.round(baselinePerDay)));
  return { startLimit: b, step: Math.max(1, Math.round(b / 10)), everyDays: 7, floor: 0 };
}

function sorted(taper: readonly TaperSegment[]): TaperSegment[] {
  return [...taper].sort((a, b) => compareDates(a.from, b.from));
}

/** The segment in force on `date` (the last one starting on or before it), or null before the plan. */
export function segmentOn(taper: readonly TaperSegment[], date: LocalDate): TaperSegment | null {
  let found: TaperSegment | null = null;
  for (const s of sorted(taper)) {
    if (compareDates(s.from, date) <= 0) found = s;
    else break;
  }
  return found;
}

function limitIn(s: TaperSegment, date: LocalDate): number {
  const steps = Math.floor(dayDiff(s.from, date) / s.everyDays);
  return Math.max(s.floor, s.startLimit - steps * s.step);
}

/** The daily limit on a local day. Steps happen at local midnight because they're computed per date. */
export function limitOn(taper: readonly TaperSegment[], date: LocalDate): number | null {
  const s = segmentOn(taper, date);
  return s ? limitIn(s, date) : null;
}

/** First day the limit is 0 under the latest segment; null if the plan cuts down to a floor or never steps. */
export function quitDateOf(taper: readonly TaperSegment[]): LocalDate | null {
  const s = sorted(taper).at(-1);
  if (!s || s.floor > 0) return null;
  if (s.startLimit === 0) return s.from;
  if (s.step === 0) return null;
  return addDays(s.from, Math.ceil(s.startLimit / s.step) * s.everyDays);
}

export interface TaperStep {
  date: LocalDate;
  limit: number;
}

/** The next limit drops strictly after `after`, at most `max`, ending with the one that reaches the floor. */
export function upcomingSteps(
  taper: readonly TaperSegment[],
  after: LocalDate,
  max = 4,
): TaperStep[] {
  const s = segmentOn(taper, after) ?? sorted(taper)[0];
  if (!s || s.step === 0) return [];
  const out: TaperStep[] = [];
  for (let k = 1; out.length < max; k += 1) {
    const date = addDays(s.from, k * s.everyDays);
    const limit = Math.max(s.floor, s.startLimit - k * s.step);
    if (compareDates(date, after) > 0) out.push({ date, limit });
    if (limit === s.floor) break;
  }
  return out;
}

/**
 * Applies a schedule edit from `today` on. Past days keep the limits they had. An edit made earlier
 * the same day is replaced. If the draft matches what the old schedule gives today, nothing is added,
 * so the old rhythm (which weekday the steps fall on) is kept.
 */
export function editTaper(
  taper: readonly TaperSegment[],
  today: LocalDate,
  draft: TaperDraft,
): TaperSegment[] {
  const kept = sorted(taper).filter((s) => compareDates(s.from, today) < 0);
  const current = segmentOn(kept, today);
  if (
    current &&
    limitIn(current, today) === draft.startLimit &&
    current.step === draft.step &&
    current.everyDays === draft.everyDays &&
    current.floor === draft.floor
  ) {
    return kept;
  }
  return [...kept, { from: today, ...draft }];
}

/** The schedule as it stands today, for prefilling the edit form. */
export function currentTaperDraft(
  taper: readonly TaperSegment[],
  today: LocalDate,
): TaperDraft | null {
  const s = segmentOn(taper, today) ?? sorted(taper)[0] ?? null;
  if (!s) return null;
  const startLimit = compareDates(s.from, today) <= 0 ? limitIn(s, today) : s.startLimit;
  return { startLimit, step: s.step, everyDays: s.everyDays, floor: s.floor };
}

/** 'smoke_free' from the quit date on; a plan without a quit date stays in 'taper'. */
export function phaseOn(taper: readonly TaperSegment[], date: LocalDate): QuitPhase {
  const q = quitDateOf(taper);
  return q !== null && compareDates(date, q) >= 0 ? 'smoke_free' : 'taper';
}
